import React, { useState, useEffect } from "react";
import {
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
  MotifSelect,
  MotifOption,
  MotifFormField,
} from "@ey-xd/motif-react";
import { CloudUpload } from "../../assets/Icons/CloudUpload";
import { CloseIcon } from "../../assets/Icons/CloseIcon";
import { FILE_UPLOAD, FILE_TYPE_OPTIONS } from "../../util/constants";
import "./UploadNewFile.scss"; // Use same styles as UploadNewFile

// import { DeleteIcon } from "../../assets/Icons/DeleteIcon";
// import { CloseIcon } from "../../assets/Icons/CloseIcon";
// import {
//   FILE_UPLOAD,
//   FILE_TYPE_OPTIONS,
//   PLACEHOLDERS,
// } from "../../util/constants";
// import "./UploadNewFile.scss"; // Use same styles as UploadNewFile

// // Allowed file extensions
// const ALLOWED_EXTENSIONS = ["xlsx", "xlsm", "xls", "txt", "csv"];

// Validation status constants
const VALIDATION_STATUS = {
  VALIDATING: "Validating",
  ALL_OK: "All ok",
  ERROR: "Error",
};

/**
 * ReplaceFileModal - Modal for replacing a file in the upload list
 * @param {boolean} show - Whether to show the modal
 * @param {function} onClose - Handler for closing the modal
 * @param {object} fileToReplace - The file being replaced (contains fileType, financialYear, etc.)
 * @param {function} onConfirmReplace - Handler when replacement is confirmed, receives the new file
 * @param {function} validateFile - Function to validate the replacement file
 * @param {boolean} isUploading - Whether a file upload is in progress
 * @param {number} uploadProgress - Upload progress percentage (0-100)
 */
export const ReplaceFileModal = ({
  show,
  onClose,
  fileToReplace,
  onConfirmReplace,
  validateFile,
  isUploading = false,
  uploadProgress = 0,
}) => {
  const [replacementFile, setReplacementFile] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!show) {
      setReplacementFile(null);
      setIsValidating(false);
    }
  }, [show]);

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  // Get file type display value for dropdown
  const getFileTypeValue = (fileType) => {
    // Map display names to option values
    const valueMap = {
      "Journal Entry (JE)": "Journal Entry (JE)",
      "Trial Balance (TB)": "Trial Balance (TB)",
      JE: "Journal Entry (JE)",
      TB: "Trial Balance (TB)",
      COA: "COA",
      DT: "Dimension table",
      "Dimension table": "Dimension table",
      Other: "Other",
    };
    return valueMap[fileType] || fileType || "";
  };

  // Handles file selection
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setIsValidating(true);
      const extension = file.name.split(".").pop().toLowerCase();

      // Get the correct fileType from fileToReplace
      const currentFileType = fileToReplace?.fileType || "";

      // Create initial file entry
      const newFile = {
        id: Date.now() + Math.random(),
        name: file.name,
        size: file.size,
        type: file.type,
        extension,
        file: file,
        fileType: currentFileType,
        financialYear: fileToReplace?.financialYear,
        validationStatus: VALIDATION_STATUS.VALIDATING,
        validationErrors: [],
        isValid: true,
        isProcessing: false,
      };

      // Validate if validateFile function is provided
      if (validateFile) {
        const validation = await validateFile(
          file,
          fileToReplace?.fileType,
        );
        newFile.validationStatus = validation.isValid
          ? VALIDATION_STATUS.ALL_OK
          : VALIDATION_STATUS.ERROR;
        newFile.validationErrors = validation.errors;
        newFile.isValid = validation.isValid;
      }

      setReplacementFile(newFile);
      setIsValidating(false);
    }
    e.target.value = "";
  };

  // Removes the replacement file
  const handleRemoveFile = () => {
    setReplacementFile(null);
  };

  // Confirms replacement - don't reset state here, let parent handle it after upload completes
  const handleConfirm = () => {
    if (replacementFile && onConfirmReplace) {
      onConfirmReplace(replacementFile);
      // Don't reset state here - the file card needs to remain visible to show upload progress
      // State will be reset when modal closes (via useEffect on show prop)
    }
  };

  // Closes modal and resets state
  const handleClose = () => {
    setReplacementFile(null);
    setIsValidating(false);
    onClose();
  };

  return (
    <MotifModal
      show={show}
      onClose={handleClose}
      size="lg"
      className="upload-new-file-modal"
    >
      <MotifModalHeader>Replace file</MotifModalHeader>
      <MotifModalBody>
        <div className="upload-modal-body-content">
          {/* Left Section - Dropdowns and File Upload */}
          <div className="upload-modal-left-section">
            {/* Dropdowns */}
            <div className="upload-modal-dropdowns-section">
              <div className="upload-modal-dropdown-group">
                <label className="upload-modal-label">
                  {FILE_UPLOAD.FINANCIAL_YEAR}
                </label>
                <MotifFormField>
                  <MotifSelect
                    className="upload-modal-dropdown disabled-dropdown"
                    value={fileToReplace?.financialYear || "CY"}
                    disabled
                    filter
                  >
                    <MotifOption value="PY">
                      {FILE_UPLOAD.PREVIOUS_YEAR}
                    </MotifOption>
                    <MotifOption value="CY">
                      {FILE_UPLOAD.CURRENT_YEAR}
                    </MotifOption>
                  </MotifSelect>
                </MotifFormField>
              </div>
              <div className="upload-modal-dropdown-group">
                <label className="upload-modal-label">
                  {FILE_UPLOAD.FILE_TYPE}
                </label>
                <MotifFormField>
                  <MotifSelect
                    className="upload-modal-dropdown disabled-dropdown"
                    value={getFileTypeValue(fileToReplace?.fileType)}
                    disabled
                    filter
                  >
                    <MotifOption value="Journal Entry (JE)">
                      {FILE_TYPE_OPTIONS.JOURNAL_ENTRY_JE}
                    </MotifOption>
                    <MotifOption value="Trial Balance (TB)">
                      {FILE_TYPE_OPTIONS.TRIAL_BALANCE_TB}
                    </MotifOption>
                    <MotifOption value="COA">
                      {FILE_TYPE_OPTIONS.COA}
                    </MotifOption>
                    <MotifOption value="Dimension table">
                      {FILE_TYPE_OPTIONS.DIMENSION_TABLE}
                    </MotifOption>
                    <MotifOption value="Other">
                      {FILE_TYPE_OPTIONS.OTHER}
                    </MotifOption>
                  </MotifSelect>
                </MotifFormField>
              </div>
            </div>

            {/* File Upload */}
            <div className="upload-modal-file-section">
              <div className="upload-modal-file-header">
                <label className="upload-modal-label">
                  {FILE_UPLOAD.FILE_UPLOAD_TITLE}
                </label>
                <span className="upload-modal-note">
                  <span className="note-link">
                    Note: Files can be uploaded only on EY network or EY
                    approved VPN.
                  </span>
                </span>
              </div>
              <input
                type="file"
                className="upload-modal-file-input"
                id="replace-file-input"
                onChange={handleFileChange}
                accept=".xlsx,.xlsm,.xls,.txt,.csv"
              />
              <label
                htmlFor="replace-file-input"
                className="upload-modal-file-dropzone"
              >
                <p className="file-upload-icon">
                  <CloudUpload width={20} height={20} color="#a4a3b1" />
                </p>
                <p>
                  <b style={{ color: "#fff" }}>{FILE_UPLOAD.CLICK_TO_UPLOAD}</b>{" "}
                  {FILE_UPLOAD.DRAG_AND_DROP}
                </p>
                <p className="normal-filetype">
                  {FILE_UPLOAD.SUPPORTED_FORMATS}
                </p>
              </label>
            </div>

            {/* Uploaded File Card */}
            {replacementFile && (
              <div
                className={`replace-file-card ${!replacementFile.isValid ? "has-error" : ""}`}
              >
                <div className="replace-file-card-info">
                  <div className="replace-file-icon">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                        stroke="#dedee2"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M14 2V8H20"
                        stroke="#dedee2"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="replace-file-details">
                    <span className="replace-file-name">
                      {replacementFile.name}
                    </span>
                    <span className="replace-file-size">
                      {formatFileSize(replacementFile.size)}
                    </span>
                    {/* Upload Progress Bar */}
                    {isUploading && (
                      <div className="replace-file-upload-progress">
                        <div className="progress-bar-container">
                          <div 
                            className="progress-bar-fill" 
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                        <span className="progress-text">
                          {uploadProgress < 100 
                            ? `Uploading... ${uploadProgress}%` 
                            : "Processing..."}
                        </span>
                      </div>
                    )}
                    {replacementFile.validationErrors?.length > 0 && (
                      <div className="replace-file-errors">
                        {replacementFile.validationErrors.map((error, idx) => (
                          <span key={idx} className="replace-file-error">
                            {error}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {!isUploading && (
                  <button
                    className="replace-file-remove-btn"
                    onClick={handleRemoveFile}
                    title="Remove file"
                  >
                    <CloseIcon width={16} height={16} color="#737387" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </MotifModalBody>
      <MotifModalFooter>
        <div className="upload-modal-footer-buttons">
          <button 
            className="upload-modal-cancel-btn" 
            onClick={handleClose}
            disabled={isUploading}
          >
            {FILE_UPLOAD.CANCEL}
          </button>
          <button
            className="upload-modal-upload-btn"
            onClick={handleConfirm}
            disabled={
              !replacementFile || !replacementFile.isValid || isValidating || isUploading
            }
          >
            {isUploading ? "Uploading..." : FILE_UPLOAD.CONTINUE}
          </button>
        </div>
      </MotifModalFooter>
    </MotifModal>
  );
};

export default ReplaceFileModal;
